import { forwardRef, type InputHTMLAttributes } from "react";
import { cn } from "../lib/cn";

/**
 * Underlined text field per the onboarding spec: mono caps label,
 * 1.5px teal-700 underline, no box border, serif inline display value
 * (Newsreader 400, 28px). The canonical first-impression input - used
 * for the "first name" prompt and any high-emphasis single-line entry.
 *
 * Browser quirks the chrome explicitly suppresses:
 *   - Chrome / Safari `:-webkit-autofill` paint a light-blue rounded
 *     background + foreign font when the browser fills a stored value.
 *     Counter with an inset box-shadow at the canvas colour and
 *     `-webkit-text-fill-color` for the glyphs; long transition delay
 *     keeps it stable through the autofill fill-animation.
 *   - Default focus outline is suppressed - the underline thickens via
 *     `focus:border-teal-700` instead, which reads cleaner on cream.
 *   - `appearance: none` kills the rounded box default Safari draws on
 *     `<input type="search">` or similar.
 */
export interface UnderlinedFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
}

export const UnderlinedField = forwardRef<HTMLInputElement, UnderlinedFieldProps>(
  function UnderlinedField({ label, hint, className, id, spellCheck = false, ...rest }, ref) {
    const inputId = id ?? `field-${label.toLowerCase().replace(/\s+/g, "-")}`;
    return (
      <div className={cn("flex flex-col gap-2", className)}>
        <label htmlFor={inputId} className="font-sans text-xs font-medium text-ink-tertiary">
          {label}
        </label>
        <input
          ref={ref}
          id={inputId}
          // Identity fields hold proper nouns; the native red squiggle reads as
          // "wrong" against the "no wrong answers" framing (ONB-14).
          spellCheck={spellCheck}
          className={cn(
            "appearance-none rounded-none bg-transparent outline-none [&:focus-visible]:[outline:none] [&:focus-visible]:[border-radius:0]",
            // Underline only: 1.5px teal-600 bottom border. All other
            // sides explicitly zero so no browser draws an edge.
            "border-x-0 border-t-0 border-b-[1.5px] border-teal-600 focus:border-teal-700",
            // Type + spacing.
            "px-0 pt-1.5 pb-3 font-sans text-[28px] leading-none tracking-[-0.015em] text-ink-primary",
            // Autofill override. Inset box-shadow paints the canvas
            // colour BEHIND the browser's autofill layer so the user
            // never sees a baby-blue pill. text-fill-color drives the
            // glyph ink because autofill ignores `color`.
            "[&:-webkit-autofill]:shadow-[inset_0_0_0_1000px_var(--color-surface-ambient)]",
            "[&:-webkit-autofill]:[-webkit-text-fill-color:var(--color-ink-primary)]",
            "[&:-webkit-autofill]:[transition:background-color_999999s_step-end,color_999999s_step-end]",
            "[&:-webkit-autofill]:caret-navy",
          )}
          {...rest}
        />
        {hint ? <p className="m-0 text-[12px] leading-relaxed text-ink-tertiary">{hint}</p> : null}
      </div>
    );
  },
);
